import { Children, type ReactNode } from 'react';
import { tw } from '@tw';

/**
 * THE page width — every full page runs it, decided 2026-08-21 when the
 * per-page shell system (`PAGE_SHELLS` / `--page-shell` / `pageShellWidth`)
 * was deleted: three widths plus a custom-property channel to the fixed bar
 * was machinery for a distinction no page needed. One width, one owner; the
 * site bar wears the same pair of classes (`SiteBar`), which is the whole
 * alignment contract now. A phone is the mobile column (`max-w-md`), a tablet
 * two card columns (`md:max-w-3xl`), which is why the pair travels together.
 *
 * A SECTION inside a page may still choose a narrower column (`PageSpread`'s
 * `lg:max-w-4xl`, a legal text's measure) — that is a section width, not the
 * page width.
 */
export const PAGE_WIDTH = 'max-w-md md:max-w-3xl deck:max-w-6xl';

/**
 * The page frame — the single owner of where a page begins and ends.
 *
 * Every full page in the product (the storefront, the main page, the legal
 * pages, media, the dashboard, 404, the error boundary) is the same object:
 * one `.sfv2` column, full height, opening at a fixed distance from the top
 * and closing above the fold of the bar. Pages arrange their own cards inside
 * it and decide nothing about the frame.
 *
 * The top edge is a value, never a leftover. This used to be
 * `my-auto`-centred, which is why two pages that both "started at pt-24" in
 * fact started wherever their own content height put them — the storefront
 * always overflows the viewport so it never centred, the main page is shorter
 * so it always did, and the two never lined up. A frame whose top depends on
 * what is inside it is not a frame.
 *
 * The top padding clears the site bar, which is fixed at the top on every
 * screen — one value at every width (`pt-beat`, 112px), because the bar is the
 * same size at every width too. `header` (80px) is the token for the bar's
 * HEIGHT; height is not clearance: clearance is height plus air, and this is
 * the one place that owns it.
 *
 * Horizontal gutter is NOT here: `<main>` in app/layout.tsx owns it for the
 * whole app.
 */
export function PageFrame({
  center = false,
  store = false,
  children,
}: {
  center?: boolean;
  /** A creator's page (or the one built for her): products and the quiz in ink, see site-theme.css. */
  store?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={tw('sfv2 flex min-h-screen flex-col', store && 'sf-store')}>
      {/* `center` sits a page that is shorter than the screen in the middle of it —
          the builder's one question — without moving where a tall page begins. */}
      <div
        className={tw(
          'w-full pb-24 pt-beat deck:pb-8',
          center && 'flex flex-1 flex-col justify-center'
        )}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * One column — the card/mobile column (`max-w-md`), the measure every card
 * and text section is set to. A column that IS the page (a page heading, a
 * page-level wrapper) passes `PAGE_WIDTH` as `className`; anything else here
 * is spacing, never a third width.
 */
export function PageColumn({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={tw('mx-auto w-full max-w-md deck:px-6', className)}>{children}</div>;
}

/**
 * The card grid: one stack on a phone, one column per child from `deck`, always
 * at the page width. The column count is read off the children — a prop
 * restating it could only agree or lie.
 *
 * Owns two of the three rhythm tokens: `gap-card` between cards and
 * `pt-headgap` from the heading block above. Sections own the third
 * (`pt-section`). All three are spacing tokens in tailwind.config.js.
 *
 * Children are `PageCardColumn`s. On mobile each column is `display: contents`,
 * so the columns disappear and every card falls into one stack — which is why
 * a page can be planned in columns without a second mobile layout.
 */
export function PageCards({ children }: { children: ReactNode }) {
  // `toArray`, not `count`: it drops nulls and booleans, so a column written as
  // `{hasTeam && <PageCardColumn/>}` cannot count as one while rendering
  // nothing — which would pick the layout for a column that is not there.
  const count = Children.toArray(children).length;
  return (
    <div
      className={tw(
        'mx-auto flex w-full flex-col gap-card pt-headgap deck:gap-8 deck:px-6',
        // Cards are card-width, the GRID is page-width: a lone card stretched
        // to the page reads as a banner. The +3rem is this element's own
        // lg:px-6 — see the `card` token for why both branches derive from it.
        count === 1 ? 'max-w-md deck:max-w-[calc(theme(maxWidth.card)_+_3rem)]' : PAGE_WIDTH,
        // A tablet holds two card measures, not three: the cards flow into two
        // balanced columns in reading order (`PageCardColumn` dissolves there).
        count === 3 &&
          tw(
            'md:block md:columns-2 md:gap-card deck:grid deck:grid-cols-3 deck:items-start',
            'md:[&>*]:contents md:[&>*>*]:mb-card md:[&>*>*]:break-inside-avoid',
            'deck:[&>*]:flex deck:[&>*>*]:mb-0'
          ),
        // Two columns are two CARDS side by side, not two halves of the page —
        // fractional columns would size a card from the viewport — so they hold
        // the card measure and pack left, level with the masthead. Three stay
        // fractional: three card measures do not fit the page.
        count === 2 &&
          'md:grid md:grid-cols-2 md:items-start deck:grid deck:grid-cols-[repeat(2,minmax(0,theme(maxWidth.card)))] deck:items-start deck:justify-start'
      )}
    >
      {children}
    </div>
  );
}

/**
 * One column of cards inside `PageCards`. On a phone the columns simply stack,
 * and on a tablet their cards flow through two balanced columns — either way
 * the page reads column after column, in the order the caller built them.
 *
 * `empty:hidden` is why the stack has no holes: a column with no cards is still
 * a flex item and would spend a `gap-card` on nothing. Above `deck` it comes
 * back, because there the empty track IS the point — see `PageCards`.
 */
export function PageCardColumn({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-card empty:hidden deck:empty:flex">{children}</div>
  );
}

/**
 * The two-column spread: what the section is about on the left, the cards that
 * say it on the right. One column on a phone.
 *
 * `lg:max-w-4xl` is a SECTION width — a text spread wants a reading measure,
 * not the full page. Cards sit on `gap-card`.
 */
export function PageSpread({ heading, children }: { heading: ReactNode; children: ReactNode }) {
  return (
    <div
      className={tw(
        'mx-auto grid w-full max-w-md gap-4 lg:grid-cols-2 lg:items-start lg:gap-10 lg:px-6',
        'lg:max-w-4xl'
      )}
    >
      <div>{heading}</div>
      <div className="flex w-full flex-col gap-card">{children}</div>
    </div>
  );
}
